import React, { useEffect, useState } from 'react';
import {
    Box, Typography, Button, Card, CardContent, Table, TableHead, TableRow, TableCell,
    TableBody, Avatar, Chip, Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, FormControl, InputLabel, Select, MenuItem, Alert, IconButton, Tooltip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import api from '../services/api';

const ROLES = { admin: { label: 'Administrador', color: 'error' }, analyst: { label: 'Analista', color: 'primary' }, viewer: { label: 'Visualizador', color: 'default' } };

const defaultForm = { name: '', email: '', password: '', role: 'analyst' };

const UsersPage = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openDialog, setOpenDialog] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(defaultForm);
    const [error, setError] = useState('');

    const fetchUsers = () => api.get('/users').then(r => setUsers(r.data.data || [])).finally(() => setLoading(false));
    useEffect(() => { fetchUsers(); }, []);

    const set = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));

    const openCreate = () => { setEditing(null); setForm(defaultForm); setError(''); setOpenDialog(true); };
    const openEdit = (user) => { setEditing(user); setForm({ name: user.name, email: user.email, password: '', role: user.role }); setError(''); setOpenDialog(true); };

    const handleSave = async () => {
        if (!form.name.trim() || !form.email.trim()) { setError('Nome e email são obrigatórios'); return; }
        if (!editing && !form.password) { setError('Senha é obrigatória para novo usuário'); return; }
        try {
            if (editing) {
                const payload = { name: form.name, role: form.role };
                await api.put(`/users/${editing._id}`, payload);
            } else {
                await api.post('/users', form);
            }
            setOpenDialog(false);
            fetchUsers();
        } catch (err) {
            setError(err.response?.data?.message || 'Erro ao salvar usuário');
        }
    };

    const toggleActive = async (user) => {
        if (!window.confirm(`${user.isActive ? 'Desativar' : 'Reativar'} o usuário ${user.name}?`)) return;
        await api.put(`/users/${user._id}`, { isActive: !user.isActive });
        fetchUsers();
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                    <Typography variant="h4" fontWeight={700}>Gestão de Usuários</Typography>
                    <Typography color="text.secondary">Gerencie os usuários da sua organização</Typography>
                </Box>
                <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>Novo Usuário</Button>
            </Box>

            <Card>
                <CardContent sx={{ p: 0 }}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Usuário</TableCell>
                                <TableCell>E-mail</TableCell>
                                <TableCell>Perfil</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Último acesso</TableCell>
                                <TableCell align="center">Ações</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4 }}>Carregando...</TableCell></TableRow>
                            ) : users.map(user => {
                                const roleInfo = ROLES[user.role] || ROLES.viewer;
                                return (
                                    <TableRow key={user._id} hover sx={{ opacity: user.isActive ? 1 : 0.5 }}>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                <Avatar sx={{ bgcolor: '#eef2fa', color: '#074960', fontWeight: 700 }}>
                                                    {user.name?.[0]?.toUpperCase()}
                                                </Avatar>
                                                <Typography variant="body2" fontWeight={600}>{user.name}</Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell>{user.email}</TableCell>
                                        <TableCell>
                                            <Chip label={roleInfo.label} color={roleInfo.color} size="small" />
                                        </TableCell>
                                        <TableCell>
                                            <Chip label={user.isActive ? 'Ativo' : 'Inativo'} color={user.isActive ? 'success' : 'default'} size="small" variant="outlined" />
                                        </TableCell>
                                        <TableCell>{user.lastLogin ? new Date(user.lastLogin).toLocaleDateString('pt-BR') : 'Nunca'}</TableCell>
                                        <TableCell align="center">
                                            <Tooltip title="Editar"><IconButton size="small" onClick={() => openEdit(user)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                                            <Tooltip title={user.isActive ? 'Desativar' : 'Reativar'}>
                                                <IconButton size="small" color={user.isActive ? 'warning' : 'success'} onClick={() => toggleActive(user)}>
                                                    {user.isActive ? <BlockIcon fontSize="small" /> : <CheckCircleIcon fontSize="small" />}
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                            {!loading && users.length === 0 && (
                                <TableRow><TableCell colSpan={6} align="center" sx={{ py: 6, color: 'text.secondary' }}>Nenhum usuário encontrado</TableCell></TableRow>
                            )}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>

            <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{editing ? 'Editar Usuário' : 'Novo Usuário'}</DialogTitle>
                <DialogContent sx={{ pt: 1 }}>
                    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <TextField label="Nome completo" value={form.name} onChange={set('name')} fullWidth required />
                        <TextField label="E-mail" type="email" value={form.email} onChange={set('email')} fullWidth required disabled={!!editing} />
                        {!editing && <TextField label="Senha" type="password" value={form.password} onChange={set('password')} fullWidth required inputProps={{ minLength: 6 }} />}
                        <FormControl fullWidth>
                            <InputLabel>Perfil</InputLabel>
                            <Select value={form.role} onChange={set('role')} label="Perfil">
                                <MenuItem value="admin">Administrador</MenuItem>
                                <MenuItem value="analyst">Analista</MenuItem>
                                <MenuItem value="viewer">Visualizador</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
                    <Button variant="contained" onClick={handleSave}>{editing ? 'Salvar' : 'Criar'}</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default UsersPage;
